// Interface language (French / English).
//
// No i18n library: the app has two languages and a few hundred strings, which
// live next to the code that shows them. Each component declares its own
// `fr` dictionary and an `en: typeof fr` twin — the type makes a missing or
// misspelt English key a compile error — and picks one with `i18n.locale`:
//
//   const t = $derived(i18n.locale === "en" ? en : fr);
//
// Long rich-text passages (About page, tutorial) branch on the locale in the
// markup instead, so their <strong>/<a> stay readable. Plain TS modules call
// `tr({ fr, en })` at the moment they build the string.
//
// The choice is stored in localStorage rather than IndexedDB: it must be known
// synchronously on the very first render, before `app.init()` has opened the
// database. Only the INTERFACE follows it — the model already answers in the
// language of the question.

export type Locale = "fr" | "en";

const KEY = "app.locale";

function detect(): Locale {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === "fr" || stored === "en") return stored;
  } catch {
    // Storage blocked: fall back to the browser language.
  }
  const lang = typeof navigator === "undefined" ? "fr" : navigator.language ?? "fr";
  return lang.toLowerCase().startsWith("fr") ? "fr" : "en";
}

class I18n {
  locale = $state<Locale>(detect());

  constructor() {
    if (typeof document !== "undefined") document.documentElement.lang = this.locale;
  }

  set(locale: Locale): void {
    this.locale = locale;
    if (typeof document !== "undefined") document.documentElement.lang = locale;
    try {
      localStorage.setItem(KEY, locale);
    } catch {
      // Not persisted: the choice still holds for this session.
    }
  }
}

export const i18n = new I18n();

/** The current-locale entry of a `{ fr, en }` pair. Reactive when read from a
 *  template or `$derived`; read once when called from plain code. */
export function tr<T>(pair: { fr: T; en: T }): T {
  return pair[i18n.locale];
}
